<i18n src="./resources/locales.json"></i18n>
<i18n src="@/constants/agent/locales.json"></i18n>

<template>
  <div class="bp-monitoring-agents-card">
    <div class="toolbar">
      <span v-once class="toolbar-label">{{ $t('agentsCard.toolbar.assignments') }}</span>

      <v-tooltip top color="secondary">
        <template v-slot:activator="{ on }">
          <v-btn icon x-small class="toolbar-btn" :disabled="disableAddAction" v-on="on" @click="openActivationDialog">
            <v-icon dark>mdi-account-plus</v-icon>
          </v-btn>
        </template>
        <span v-once>{{ $t('agentsCard.toolbar.tmpAssign') }}</span>
      </v-tooltip>

      <v-tooltip top color="secondary">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            x-small
            class="toolbar-btn"
            :disabled="disableRemoveAction"
            v-on="on"
            @click="openDeactivationDialog"
          >
            <v-icon dark>mdi-account-minus</v-icon>
          </v-btn>
        </template>
        <span v-once>{{ $t('agentsCard.toolbar.tmpRemove') }}</span>
      </v-tooltip>

      <v-tooltip top color="secondary" z-index="130">
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            x-small
            class="toolbar-btn"
            :disabled="disableExpirationAction"
            v-on="on"
            @click="openAssignmentsExpirationTimeDialog"
          >
            <v-icon dark>mdi-calendar-end</v-icon>
          </v-btn>
        </template>
        <span v-once>{{ $t('agentsCard.toolbar.setNewExpirationTime') }}</span>
      </v-tooltip>

      <v-tooltip top color="secondary">
        <template v-slot:activator="{ on }">
          <v-btn icon x-small class="toolbar-btn" :disabled="disableResetAction" v-on="on" @click="resetAssignments">
            <v-icon dark>mdi-undo</v-icon>
          </v-btn>
        </template>
        <span v-once>{{ $t('agentsCard.toolbar.resetAssignments') }}</span>
      </v-tooltip>

      <v-spacer></v-spacer>

      <ecs-search-input
        ref="searchInput"
        :placeholder="$t('agentsCard.toolbar.toolbarSearchPlaceholder')"
        class="toolbar-search"
        :filters="filterOptions"
        small
        sync
        @search-input-changed="handleSearch"
        @search-filters-changed="handleSearchFilters"
      />

      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon x-small class="toolbar-btn" v-bind="attrs" v-on="on">
            <v-icon dark>mdi-sort-variant</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-for="item in sortOptions" :key="item.key" @click="item.action">
            <v-list-item-icon class="menu-icon">
              <v-icon v-show="selectedSortOption === item.key" small color="success">mdi-check-bold</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div class="toolbar-shadow inset-fade-effect" :dark="$vuetify.theme.dark" />
    <v-progress-linear v-show="isLoading" class="loading-progress-bar" indeterminate color="primary" height="3" />

    <div class="content custom-scrollbar">
      <EcsBusinessProcessMonitoringAgent
        v-for="agent in visibleAgents"
        :key="agent.id"
        :agent="agent"
        :dark="$vuetify.theme.dark"
      />
      <div v-if="showNoAgentsMessage" class="no-agents">
        {{ $t('noAgents') }}
      </div>
    </div>

    <EcsAgentAssignmentDialog
      ref="temporaryAgentActivationDialog"
      :title="$t('dialogs.agentsActivation.title')"
      :description="$t('dialogs.agentsActivation.description', { businessProcessName })"
      :search-placeholder="$t('dialogs.agentsActivation.searchPlaceholder')"
      :save-button-text="$t('options.activate')"
      :agents="inactiveAgents"
      @save-changes="handleAgentsActivation"
    />
    <EcsAgentAssignmentDialog
      ref="temporaryAgentDeactivationDialog"
      :title="$t('dialogs.agentsDeactivation.title')"
      :description="$t('dialogs.agentsDeactivation.description', { businessProcessName })"
      :search-placeholder="$t('dialogs.agentsDeactivation.searchPlaceholder')"
      :save-button-text="$t('options.deactivate')"
      :agents="activeAgents"
      @save-changes="handleAgentsDeactivation"
    />
    <EcsTempAssignExpirationDialog
      ref="assignmentsExpirationTimeDialog"
      :business-process-id="businessProcessId"
      :agents="temporaryAssignments"
      @update-assignments="handleTempAssignmentsUpdate"
    />
    <EcsConfirmationDialog
      ref="resetAssignmentsConfirmationDialog"
      :title="$t('dialogs.resetAssignments.title')"
      :message="$t('dialogs.resetAssignments.description', { businessProcessName })"
      :confirm-button-text="$t('options.accept')"
      :cancel-button-text="$t('options.cancel')"
    />
  </div>
</template>

<script>
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import { mapGetters } from 'vuex';
import { AGENT_STATE } from '@/constants/agent/constants';
import businessProcessService from '@/services/businessProcessService';
import EcsBusinessProcessMonitoringAgent from '@/components/ecs-business-process-monitoring-card/EcsBusinessProcessMonitoringAgent/EcsBusinessProcessMonitoringAgent';
import EcsState from '@/components/ecs-agent-state/EcsState';
import EcsAgentAssignmentDialog from '@/components/ecs-business-process-monitoring-card/EcsAgentAssignmentDialog';
import EcsTempAssignExpirationDialog from '@/components/ecs-business-process-monitoring-card/EcsTempAssignExpirationDialog';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';
import EcsBusinessProcessHealthMixins from '@/mixins/ecs-business-process-health/EcsBusinessProcessHealthMixins';

const SNACKBAR_MESSAGE_EVENT = 'snackbar-message';

export default {
  name: 'EcsBusinessProcessMonitoringAgentsCard',

  components: {
    EcsBusinessProcessMonitoringAgent,
    EcsAgentAssignmentDialog,
    EcsTempAssignExpirationDialog,
    EcsConfirmationDialog
  },

  mixins: [EcsBusinessProcessHealthMixins],

  props: {
    businessProcessId: {
      type: String,
      required: true
    },
    data: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      agents: [],
      searchString: '',
      activeFilters: {},
      visibleAgents: [],
      selectedSortOption: 'STATE',
      isLoading: false,
      sortOptions: [],
      filterOptions: []
    };
  },

  sockets: {
    AGENT_STATE_CHANGED_EVENT() {
      this.refreshVisibleAgents();
    },
    AGENT_BP_ASSIGNMENT_CHANGED_EVENT() {
      this.refreshVisibleAgents();
    }
  },

  computed: {
    ...mapGetters('CONTACTS', ['getContact']),

    ...mapGetters('REFERENCES', ['availableCustomBreakStates']),

    businessProcessName() {
      return this.businessProcess.businessProcessName;
    },

    activeAgents() {
      return this.agents.filter(agent => agent.isActive);
    },

    inactiveAgents() {
      return this.agents.filter(agent => !agent.isActive);
    },

    temporaryAssignments() {
      return this.agents.filter(agent => !!agent.expirationTime);
    },

    hasTemporaryAssignments() {
      return !!this.temporaryAssignments.length;
    },

    showNoAgentsMessage() {
      return this.visibleAgents.length === 0 && !this.isLoading;
    },

    disableAddAction() {
      return this.isLoading || !this.inactiveAgents.length;
    },

    disableRemoveAction() {
      return this.isLoading || !this.activeAgents.length;
    },

    disableExpirationAction() {
      return this.isLoading || !this.hasTemporaryAssignments;
    },

    disableResetAction() {
      return this.isLoading || !this.hasTemporaryAssignments;
    }
  },

  watch: {
    selectedSortOption() {
      this.computeVisibleAgents();
    },

    agents() {
      this.computeVisibleAgents();
    }
  },

  async activated() {
    await this.$nextTick();
    if (this.data) {
      const { searchString, agentStateId } = this.data;

      if (searchString) {
        this.$refs.searchInput.searchText = searchString;
      }

      if (agentStateId) {
        this.$refs.searchInput.clearFilters();
        this.$refs.searchInput.setActiveFilter('STATE_FILTER', agentStateId);
      }
    }
  },

  created() {
    this.initialize();
  },

  mounted() {
    this.reloadAgents();
  },

  methods: {
    reloadAgents() {
      this.isLoading = true;
      businessProcessService
        .getBusinessProcessAssignments(this.businessProcessId)
        .then(({ data }) => {
          this.agents = orderBy(data, [o => o.isActive, o => o.name], ['desc', 'asc']);
          this.computeVisibleAgents();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    initialize() {
      this.sortOptions = [
        {
          key: 'ASC',
          title: this.$t('agentsCard.sort.alphaAsc'),
          action: () => (this.selectedSortOption = 'ASC')
        },
        {
          key: 'DESC',
          title: this.$t('agentsCard.sort.alphaDesc'),
          action: () => (this.selectedSortOption = 'DESC')
        },
        {
          key: 'STATE',
          title: this.$t('agentsCard.sort.agentState'),
          action: () => (this.selectedSortOption = 'STATE')
        }
      ];

      this.filterOptions = [
        {
          key: 'STATE_FILTER',
          title: this.$t('agentsCard.filters.agentState'),
          options: [
            { label: this.$t(AGENT_STATE.ready), value: AGENT_STATE.ready },
            { label: this.$t(AGENT_STATE.busy), value: AGENT_STATE.busy },
            { label: this.$t(AGENT_STATE.offline), value: AGENT_STATE.offline },
            { label: this.$t(AGENT_STATE.waitingForOffline), value: AGENT_STATE.waitingForOffline },
            { label: this.$t(AGENT_STATE.backoffice), value: AGENT_STATE.backoffice },
            { label: this.$t(AGENT_STATE.waitingForBackoffice), value: AGENT_STATE.waitingForBackoffice },
            { label: this.$t(AGENT_STATE.break), value: AGENT_STATE.break },
            { label: this.$t(AGENT_STATE.waitingForBreak), value: AGENT_STATE.waitingForBreak },
            { label: this.$t(AGENT_STATE.outgoing), value: AGENT_STATE.outgoing },
            { label: this.$t(AGENT_STATE.waitingForOutgoing), value: AGENT_STATE.waitingForOutgoing },
            { label: this.$t(AGENT_STATE.dialing), value: AGENT_STATE.dialing },
            { label: this.$t(AGENT_STATE.offered), value: AGENT_STATE.offered },
            { label: this.$t(AGENT_STATE.noAnswer), value: AGENT_STATE.noAnswer },
            ...this.availableCustomBreakStates.map(customBreak => {
              return { label: `${this.$t(AGENT_STATE.break)} (${customBreak.name})`, value: customBreak.id };
            })
          ]
        },
        {
          key: 'ASSIGNMENT_FILTER',
          title: this.$t('agentsCard.filters.assignment.status'),
          options: [
            { label: this.$t('agentsCard.filters.assignment.active'), value: 'ACTIVE' },
            { label: this.$t('agentsCard.filters.assignment.inactive'), value: 'INACTIVE' }
          ]
        }
      ];
    },

    async refreshVisibleAgents() {
      await this.$nextTick();
      if (this.selectedSortOption === 'STATE') {
        this.computeVisibleAgents();
      }
    },

    computeVisibleAgents() {
      this.visibleAgents = (this.agents || [])
        .filter(agent => agent.isActive || !!agent.expirationTime)
        .filter(this.agentNameFilter)
        .filter(this.selectedFilters)
        .sort(this.sortCriteria);
    },

    handleSearch(searchString) {
      this.searchString = searchString;
      this.computeVisibleAgents();
    },

    handleSearchFilters(activeFilters) {
      this.activeFilters = activeFilters;
      this.computeVisibleAgents();
    },

    sortByNameAsc(agentA, agentB) {
      if (agentA.name > agentB.name) {
        return 1;
      }
      if (agentA.name < agentB.name) {
        return -1;
      }
      return 0;
    },

    sortByNameDesc(agentA, agentB) {
      if (agentA.name > agentB.name) {
        return -1;
      }
      if (agentA.name < agentB.name) {
        return 1;
      }
      return 0;
    },

    sortByState(agentA, agentB) {
      if (!agentA || !agentB) {
        return null;
      }
      const contactA = this.getContact(agentA.id);
      const contactB = this.getContact(agentB.id);
      if (!contactA || !contactB) {
        return null;
      }

      const stateOrder = [
        ...EcsState.readyStateIds,
        ...EcsState.busyStateIds,
        ...EcsState.outgoingStateIds,
        ...EcsState.backofficeStateIds,
        ...EcsState.breakStateIds,
        ...EcsState.offlineStateIds
      ];

      const agentAStateIndex = contactA.state
        ? stateOrder.findIndex(stateId => stateId === contactA.state.id)
        : stateOrder.length;
      const agentBStateIndex = contactB.state
        ? stateOrder.findIndex(stateId => stateId === contactB.state.id)
        : stateOrder.length;

      if (agentAStateIndex > agentBStateIndex) {
        return 1;
      }
      if (agentAStateIndex < agentBStateIndex) {
        return -1;
      }
      return 0;
    },

    agentNameFilter(agent) {
      return agent.name.toLowerCase().includes(this.searchString.toLowerCase());
    },

    selectedFilters(agent) {
      const matches = [];

      if (this.activeFilters.STATE_FILTER) {
        const contact = this.getContact(agent.id);
        const contactStateId = (contact.state || {}).id;
        const match = this.activeFilters.STATE_FILTER.find(stateId => stateId === contactStateId);
        matches.push(match);
      }

      if (this.activeFilters.ASSIGNMENT_FILTER) {
        const agentAssignment = agent.isActive ? 'ACTIVE' : 'INACTIVE';
        const match = this.activeFilters.ASSIGNMENT_FILTER.find(assignment => assignment === agentAssignment);
        matches.push(match);
      }

      return Object.keys(this.activeFilters).length ? matches.every(filter => filter) : true;
    },

    sortCriteria(agentA, agentB) {
      switch (this.selectedSortOption) {
        case 'ASC':
          return this.sortByNameAsc(agentA, agentB);
        case 'DESC':
          return this.sortByNameDesc(agentA, agentB);
        case 'STATE':
          return this.sortByState(agentA, agentB);
      }
      return null;
    },

    openActivationDialog() {
      this.$refs.temporaryAgentActivationDialog.open();
    },

    openDeactivationDialog() {
      this.$refs.temporaryAgentDeactivationDialog.open();
    },

    openAssignmentsExpirationTimeDialog() {
      this.$refs.assignmentsExpirationTimeDialog.open();
    },

    handleAgentsActivation(data) {
      const { agents, duration } = data;
      this.isLoading = true;
      this.agents = [];
      businessProcessService
        .activateAgents(
          this.businessProcessId,
          agents.map(agent => agent.id),
          duration
        )
        .then(() => {
          this.reloadAgents();
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('success.agentsActivated', { businessProcessName: this.businessProcessName }),
            type: 'success'
          });
        })
        .catch(() => {
          this.isLoading = false;
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('errors.errorOnAgentsActivation'),
            type: 'error'
          });
        });
    },

    handleAgentsDeactivation(data) {
      const { agents, duration } = data;
      this.isLoading = true;
      this.agents = [];
      businessProcessService
        .deactivateAgents(
          this.businessProcessId,
          agents.map(agent => agent.id),
          duration
        )
        .then(() => {
          this.reloadAgents();
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('success.agentsDeactivated', { businessProcessName: this.businessProcessName }),
            type: 'success'
          });
        })
        .catch(() => {
          this.isLoading = false;
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('errors.errorOnAgentsDeactivation'),
            type: 'error'
          });
        });
    },

    handleTempAssignmentsUpdate({ businessProcessId, agentIds, expirationTime }) {
      this.isLoading = true;
      this.agents = [];

      businessProcessService
        .setTemporaryAssignmentsExpiration(businessProcessId, agentIds, expirationTime)
        .then(() => {
          this.reloadAgents();
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('success.updateAssignmentsExpirationTime'),
            type: 'success'
          });
        })
        .catch(() => {
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('errors.errorOnUpdateAssignmentsExpirationTime'),
            type: 'error'
          });
        });
    },

    async resetAssignments() {
      const confirmed = await this.$refs.resetAssignmentsConfirmationDialog.open();
      if (confirmed) {
        this.isLoading = true;
        this.agents = [];
        const expirationTime = moment().format('YYYY-MM-DD hh:mm:ss');
        const agentIds = this.agents.filter(agent => !!agent.expirationTime).map(agent => agent.id);
        businessProcessService
          .setTemporaryAssignmentsExpiration(this.businessProcessId, agentIds, expirationTime)
          .then(() => {
            this.reloadAgents();
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('success.resetAgentAssignments', { businessProcessName: this.businessProcessName }),
              type: 'success'
            });
          })
          .catch(() => {
            this.isLoading = false;
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('errors.errorOnResetAssignments'),
              type: 'error'
            });
          });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.bp-monitoring-agents-card {
  display: grid;
  grid-template-rows: 32px calc(100% - 32px);
  height: 100%;
  position: relative;

  .toolbar {
    display: flex;
    height: 32px !important;
    background-color: var(--v-surface-lighten1);

    .toolbar-label {
      line-height: 32px;
      font-size: 12px;
      opacity: 0.75;
      padding: 0 8px;
    }

    .toolbar-btn {
      width: 26px;
      height: 26px;
      margin: 4px 2px;

      i {
        font-size: 16px;
      }

      ::v-deep .v-progress-circular {
        width: 18px !important;
        height: 18px !important;
      }
    }

    .toolbar-search {
      width: 260px;
      margin: 2px;
      border: 0;
    }
  }

  .toolbar-shadow {
    top: 32px;
  }

  .loading-progress-bar {
    position: absolute;
    top: 32px;
    z-index: 1;
  }

  .content {
    background-color: var(--v-surface-lighten1);
    border-radius: 0 0 6px 6px;
    border-top: 1px solid var(--v-border-color);
    position: relative;
  }
}

[dark] .bp-monitoring-agents-card .content {
  background-color: var(--v-surface-darken1);
  border-top: none;
}

.no-agents {
  text-align: center;
  font-size: 12px;
  padding: 20px 0;
  opacity: 0.5;
}
</style>

<style>
.menu-icon {
  margin-right: 8px !important;
}
</style>
